import React, { FocusEventHandler, useCallback, useMemo } from 'react';

import { css, cx } from '@emotion/css';
import { useTheme } from '@emotion/react';
import isHotkey from 'is-hotkey';
import { Editor, Node, Range, Transforms, createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, useSlate, withReact } from 'slate-react';
import { Theme } from 'theme';

import { CommonComponentProps } from '../../types/components';
import { Size, matchSize } from '../../utility/common-props-helpers';
import FlexRow from '../FlexRow';
import ListItem from '../ListItem';
import Popover from '../Popover';
import SvgIcon from '../SvgIcon';
import { Tokens } from './formatRichText';
import icBold from './ic-bold.svg';
import icEmail from './ic-email.svg';
import icHttp from './ic-http.svg';
import icItalic from './ic-italic.svg';
import icLink from './ic-link.svg';
import icUnderline from './ic-underline.svg';

const getTextEditorStyles = ({
    theme,
    disabled,
    size = 'md',
    style,
    error,
}: Pick<Props, 'theme' | 'style' | 'disabled' | 'size' | 'error'>) =>
    cx(
        css({
            fontSize: '1.6rem',
            color: theme.color.jetBlack,
            background: theme.color.secondary1,
            borderRadius: theme.borderRadius.base,
            lineHeight: matchSize({ md: '22px' })(size),
            width: '100%',
            height: 252,
            padding: '14px 20px',
            overflowY: 'scroll',
            ...(disabled && {
                color: theme.color.disabledText,
            }),
            border: error ? '1px solid ' + theme.color.danger1 : 'none',
            ...(style as any),
            outline: 'none',
            boxShadow: 'none',
        })
    );

const HOTKEYS = {
    'mod+b': 'bold',
    'mod+i': 'italic',
    'mod+u': 'underline',
    'mod+l': 'link',
    'mod+e': 'email',
};

const LIST_TYPES = ['numbered-list', 'bulleted-list'];

export const RICH_TEXT_EDITOR_DEFAULT_VALUE = [
    {
        type: 'paragraph',
        children: [{ text: '' }],
    },
];

type FormatType = 'bold' | 'italic' | 'underline';
type BlockType = 'link' | 'token' | 'email';

type Props = CommonComponentProps & {
    size?: Size;
    disabled?: boolean;
    autoFocus?: boolean;
    theme: Theme;
    value: Node[];
    onChange: (value: Node[]) => void;
    onBlur?: FocusEventHandler<HTMLDivElement>;
    error?: any;
};

const RichTextEditor = ({ value = RICH_TEXT_EDITOR_DEFAULT_VALUE, ...props }: Props) => {
    const renderElement = useCallback((props) => <Element {...props} />, []);
    const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
    const editor = useMemo(() => withLinks(withHistory(withReact(createEditor()))), []);
    const theme = useTheme();

    return (
        <Slate editor={editor} value={value} initialValue={value} {...props}>
            <Editable
                className={getTextEditorStyles({ ...props, theme })}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                placeholder=""
                spellCheck
                onKeyDown={(event) => {
                    for (const hotkey in HOTKEYS) {
                        if (isHotkey(hotkey, event as any)) {
                            event.preventDefault();
                            const mark = HOTKEYS[hotkey];
                            toggleMark(editor, mark);
                        }
                    }
                }}
                onBlur={props?.onBlur}
            />
            <FlexRow style={{ marginTop: 18, marginLeft: 15 }} align="center" itemsSpacing={14}>
                <FlexRow align="center" itemsSpacing={10}>
                    <MarkButton format="bold" icon={icBold} />
                    <MarkButton format="italic" icon={icItalic} />
                    <MarkButton format="underline" icon={icUnderline} />
                </FlexRow>
                <InsertLinkButton icon={icLink} />
                <InsertEmailButton icon={icEmail} />
                <TokensDropdown />
            </FlexRow>
        </Slate>
    );
};

const withLinks = (editor) => {
    const { isInline } = editor;

    editor.isInline = (element) => {
        return ['link', 'email', 'token'].includes(element.type) ? true : isInline(element);
    };

    return editor;
};

const isMarkActive = (editor, format: FormatType) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};

const toggleMark = (editor, format: FormatType) => {
    const isActive = isMarkActive(editor, format);

    if (isActive) {
        Editor.removeMark(editor, format);
    } else {
        Editor.addMark(editor, format, true);
    }
};

const isBlockActive = (editor, type: BlockType) => {
    const [match] = Editor.nodes(editor, {
        match: (n) => n.type === type,
    });

    return !!match;
};

const toggleBlock = (editor, type: BlockType) => {
    const isActive = isBlockActive(editor, type);
    const isList = LIST_TYPES.includes(type);

    Transforms.unwrapNodes(editor, {
        match: (n) => LIST_TYPES.includes(n.type as string),
        split: true,
    });

    Transforms.setNodes(editor, {
        type: isActive ? 'paragraph' : isList ? 'list-item' : type,
    });

    if (!isActive && isList) {
        const block = { type: type, children: [] };
        Transforms.wrapNodes(editor, block);
    }
};

const insertBlock = (editor, type: BlockType, value) => {
    if (editor.selection) {
        const node = {
            type: type,
            [type]: value,
            children: [{ text: value }],
        };

        Transforms.insertNodes(editor, node);
    }
};

const removeBlock = (editor, type: BlockType) => {
    Transforms.removeNodes(editor, { match: (n) => n.type === type });
};

const unwrapBlock = (editor, type: BlockType) => {
    Transforms.unwrapNodes(editor, { match: (n) => n.type === type });
};

const wrapBlock = (editor, type: BlockType, value) => {
    if (isBlockActive(editor, type)) {
        unwrapBlock(editor, type);
    }

    const node = {
        type: type,
        [type]: value,
        children: [],
    };

    Transforms.wrapNodes(editor, node, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
};

const Element = ({ attributes, children, element }) => {
    switch (element.type) {
        case 'paragraph':
            return <div {...attributes}>{children}</div>;
        case 'bulleted-list':
            return <ul {...attributes}>{children}</ul>;
        case 'numbered-list':
            return <ol {...attributes}>{children}</ol>;
        case 'list-item':
            return <li {...attributes}>{children}</li>;
        case 'link':
            return (
                <a {...attributes} href={element[element.type]}>
                    {children}
                </a>
            );
        case 'email':
            return (
                <a {...attributes} href={`mailto:${element[element.type]}`}>
                    {children}
                </a>
            );
        case 'token':
            return <TokenElement {...attributes}>{children}</TokenElement>;

        default:
            return children;
    }
};

const TokenElement = React.forwardRef(({ children, ...props }, ref) => {
    return (
        <code ref={ref} {...props}>
            {children}
        </code>
    );
});

const Leaf = ({ attributes, children, leaf }) => {
    if (leaf.bold) {
        children = <strong>{children}</strong>;
    }
    if (leaf.italic) {
        children = <em>{children}</em>;
    }
    if (leaf.underline) {
        children = <u>{children}</u>;
    }

    return <span {...attributes}>{children}</span>;
};

const MarkButton = ({ format, icon }) => {
    const editor = useSlate();
    const theme = useTheme();
    return (
        <SvgIcon
            size="xl"
            src={icon}
            color={() => (isMarkActive(editor, format) ? theme.color.green1 : theme.color.gray40)}
            onMouseDown={(event) => {
                event.preventDefault();
                toggleMark(editor, format);
            }}
        />
    );
};

const InsertLinkButton = ({ icon }) => {
    const editor = useSlate();
    const theme = useTheme();
    return (
        <SvgIcon
            size="xl"
            src={icon}
            color={() => (isBlockActive(editor, 'link') ? theme.color.green1 : theme.color.gray40)}
            onMouseDown={(event) => {
                event.preventDefault();
                if (isBlockActive(editor, 'link')) {
                    unwrapBlock(editor, 'link');
                    return;
                } else {
                    const url = window.prompt('Enter the URL of the link:');
                    if (!url) return;

                    const { selection } = editor;
                    const isCollapsed = selection && Range.isCollapsed(selection);

                    if (isCollapsed) {
                        insertBlock(editor, 'link', url);
                    } else {
                        wrapBlock(editor, 'link', url);
                    }
                }
            }}
        />
    );
};
const InsertEmailButton = ({ icon }) => {
    const editor = useSlate();
    const theme = useTheme();
    return (
        <SvgIcon
            size="xl"
            src={icon}
            color={() => (isBlockActive(editor, 'email') ? theme.color.green1 : theme.color.gray40)}
            onMouseDown={(event) => {
                event.preventDefault();
                if (isBlockActive(editor, 'email')) {
                    unwrapBlock(editor, 'email');
                    return;
                } else {
                    const url = window.prompt('Enter the Email:');
                    if (!url) return;

                    insertBlock(editor, 'email', url);
                }
            }}
        />
    );
};

const InsertTokenButton = ({ token, text }) => {
    const editor = useSlate();
    return (
        <ListItem
            onMouseDown={(event) => {
                event.preventDefault();
                insertBlock(editor, 'token', token);
            }}
        >
            {text}
        </ListItem>
    );
};

const RemoveTokenButton = ({ text }) => {
    const editor = useSlate();
    return (
        <ListItem
            onMouseDown={(event) => {
                event.preventDefault();
                removeBlock(editor, 'token');
            }}
        >
            {text}
        </ListItem>
    );
};

const TokensDropdown = () => {
    const editor = useSlate();
    const theme = useTheme();
    return (
        <Popover
            renderContent={() =>
                isBlockActive(editor, 'token') ? (
                    <RemoveTokenButton token={Tokens.USER_NAME} text="Remove" />
                ) : (
                    <>
                        <InsertTokenButton token={Tokens.USER_NAME} text="User Name" />
                        <InsertTokenButton
                            token={Tokens.ORGANIZATION_NAME}
                            text="Organization Name"
                        />
                        <InsertTokenButton
                            token={Tokens.LICENSE_EXPIRATION_DATE}
                            text="License Expiration Date"
                        />
                    </>
                )
            }
        >
            {({ ref, toggleVisibility }) => (
                <div ref={ref}>
                    <SvgIcon
                        size="xl"
                        color={() =>
                            isBlockActive(editor, 'token') ? theme.color.green1 : theme.color.gray40
                        }
                        src={icHttp}
                        tooltip={
                            'Each instance of "User Name" will display the viewer’s full name. "Organization Name" will display the viewer’s organization name. "License Expiration Date" will display the viewer’s license expiration date.'
                        }
                        onMouseDown={(ev) => {
                            ev.preventDefault();
                            toggleVisibility();
                        }}
                    />
                </div>
            )}
        </Popover>
    );
};

export default RichTextEditor;
